import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { Address, Country, CountryNameFragment, useAddressDetailQuery } from "../../../../Utilities";
import { AddressDeleteButton } from "../../../DeleteButtons";
import { AddressFormat, CountryFormat } from "../../../Format";
import { AddressForm } from "../../../Forms";
import { AddressDeletePermissionRequired } from "../PermissionRequired";
import styles from "./styles.module.css";

export type CardAddress = Pick<
	Address,
	"id" | "name" | "addressLine1" | "addressLine2" | "city" | "postalCode" | "stateOrProvince"
> & {
	country: Pick<Country, "id"> & CountryNameFragment;
};

export type AddressCardProps = {
	address: CardAddress;
	slim?: boolean;
	editable?: boolean;
	hidePeople?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function AddressCard({
	address,
	slim = false,
	editable = false,
	hidePeople,
	className,
	style,
}: AddressCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Column
				justify="spaced-start"
				verticalSpacing="0.375rem"
				className={makeClassName(styles["address-card"], slim ? styles["address-card--slim"] : "", className)}
				style={style}
			>
				<Row justify="space-between" align="center" overflow="wrap" withVerticalSpacing>
					<Conditional>
						<If condition={slim}>
							<TruncatedText className={styles["address-card__name"]}>
								{AddressFormat.Fields.name(address.name)}
							</TruncatedText>
						</If>
						<Else>
							<Row justify="spaced-start" align="center">
								<IconContainer color="var(--theme-colors__blue--light)">
									<Icon.House color="var(--theme-colors__blue)" size={18} />
								</IconContainer>

								<TruncatedText className={styles["address-card__name"]}>
									{AddressFormat.Fields.name(address.name)}
								</TruncatedText>
							</Row>
						</Else>
					</Conditional>

					<If condition={editable}>
						<Button
							variant="link"
							size="small"
							onClick={openModal}
							className="button--icon"
							style={{ marginLeft: "auto" }}
						>
							<Icon.PencilSimpleLine size={16} className="block" />
						</Button>
					</If>
				</Row>

				<span>{AddressFormat.Fields.addressLine1(address.addressLine1)}</span>
				<If condition={!!address.addressLine2}>
					<TruncatedText>{AddressFormat.Fields.addressLine2(address.addressLine2)}</TruncatedText>
				</If>
				<Grid tracks={2} gap="0.25rem">
					<TruncatedText>{AddressFormat.Fields.city(address.city)}</TruncatedText>
					<TruncatedText>{AddressFormat.Fields.postalCode(address.postalCode)}</TruncatedText>
					<TruncatedText>{AddressFormat.Fields.stateOrProvince(address.stateOrProvince)}</TruncatedText>
					<TruncatedText>{CountryFormat.name(address.country)}</TruncatedText>
				</Grid>
			</Column>

			<Modal.If condition={isModalOpen}>
				<EditModal addressID={address.id} hidePeople={hidePeople} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	addressID: Address["id"];
	hidePeople?: boolean;
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

function EditModal({ addressID, hidePeople, ifRef, onClose, className, style }: EditModalProps) {
	const { loading, data } = useAddressDetailQuery({ variables: { id: addressID } });
	const address = data?.address;

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Address</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={loading && !data}>
						<div style={{ width: "var(--general__field--double---width)" }}>Loading...</div>
					</If>
					<Else>
						{!!address && (
							<Column justify="spaced-start">
								<AddressForm.ControlledDetail
									address={address}
									formStateArgs={editFormStateArgs}
									hidePeople={hidePeople}
									noChangesDialog
									onSuccess={onClose}
								/>

								<AddressDeletePermissionRequired>
									<AddressDeleteButton instanceID={addressID} size="small" onSuccess={onClose} />
								</AddressDeletePermissionRequired>
							</Column>
						)}
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
